import React from 'react';
import type { FormConfig } from '@tencent/wb-ext-form';
import { FormItemName, FormItemLabelName, FormItemType } from '@framework/workbench/drawer/constants';
import i18n from '@i18next/i18n';
import {
    LINEAR_GRADIENT_ICON,
    RADIAL_GRADIENT_ICON,
    RECT_GRADIENT_ICON,
    SHAPE_GRADIENT_ICON,
} from '@editor/src/img/pageIndex';
import { UPLOAD_ICON } from '@framework/workbench/drawer/image';
import { OpacityOptions, AngleOptions } from '@framework/workbench/drawer/common/config';
import { DirectList } from '@src/framework/editor/src/js/constants';
import { FillType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { angleStyle, buttonStyle, iconStyle, textStyle } from '@framework/workbench/drawer/ui/fill/constants';

export const LINEAR_GRADIENT = 'linearGradient';
export const RADIAL_GRADIENT = 'radialGradient';
export const RECT_GRADIENT = 'rectGradient';
export const SHAPE_GRADIENT = 'shapeGradient';

// 填充下拉对应的key
export const FillSelectKey = {
    NO_SELECT: '-1',
    NO_FILL: '1',
    SOLID_FILL: '2',
    GRADUAL_FILL: '3',
    IMAGE_FILL: '4',
    PATT_FILL: '5',
};

// 填充类型key对应的填充下拉key
export const FillTypeToSelectKeyMap = {
    [FillType.NoFill]: FillSelectKey.NO_FILL,
    [FillType.SolidFill]: FillSelectKey.SOLID_FILL,
    [FillType.GradualFill]: FillSelectKey.GRADUAL_FILL,
    [FillType.ImageFill]: FillSelectKey.IMAGE_FILL,
    [FillType.PattFill]: FillSelectKey.PATT_FILL,
};

// 填充类型key对应的model存储属性key
export const FillTypeToModelMap = {
    [FillType.NoFill]: FillType.NoFill,
    [FillType.SolidFill]: 'color',
    [FillType.GradualFill]: FillType.GradualFill,
    [FillType.ImageFill]: FillType.ImageFill,
    [FillType.PattFill]: FillType.PattFill,
};

// 填充下拉options数据
export const FillSelectTypes = [
    { key: FillSelectKey.NO_FILL, content: i18n.t('无填充') },
    { key: FillSelectKey.SOLID_FILL, content: i18n.t('纯色填充') },
    { key: FillSelectKey.GRADUAL_FILL, content: i18n.t('渐变填充') },
    { key: FillSelectKey.IMAGE_FILL, content: i18n.t('图片填充') },
    { key: FillSelectKey.PATT_FILL, content: i18n.t('图案填充') },
];

export const DirectOptions = DirectList.map((item: any) => {
    item.key = item;
    item.content = item.title;
    return item;
});

export const FORM_CONFIGS: FormConfig[] = [
    {
        type: FormItemType.CUSTOM,
        name: FormItemName.FONT_FILL_SELECT,
        preventFocus: true,
    },
    {
        type: FormItemType.CUSTOM,
        name: FormItemName.FONT_SOLID_FILL_COLOR,
        style: {
            boxSizing: 'content-box',
            maxHeight: 28,
        },
        preventFocus: true,
    },
    {
        type: FormItemType.TEXT,
        name: FormItemName.FONT_SOLID_FILL_COLOR_TEXT,
        value: i18n.t('颜色'),
        style: textStyle,
    },
    {
        type: FormItemType.SLIDER,
        name: FormItemName.FONT_SOLID_FILL_OPACITY,
        label: FormItemLabelName.OPACITY,
        options: {
            ...OpacityOptions,
        },
        defaultValue: 0,
    },
    {
        type: FormItemType.BUTTON,
        name: FormItemName.FONT_GRADUAL_FILL_LINEAR_GRADIENT,
        options: {
            icon: <img src={LINEAR_GRADIENT_ICON} alt={''} style={iconStyle}/>,
        },
        tooltip: i18n.t('线性'),
        style: buttonStyle,
    },
    {
        type: FormItemType.BUTTON,
        name: FormItemName.FONT_GRADUAL_FILL_RADIAL_GRADIENT,
        options: {
            icon: <img src={RADIAL_GRADIENT_ICON} alt={''} style={iconStyle}/>,
        },
        tooltip: i18n.t('射线'),
        style: buttonStyle,
    },
    {
        type: FormItemType.BUTTON,
        name: FormItemName.FONT_GRADUAL_FILL_RECT_GRADIENT,
        options: {
            icon: <img src={RECT_GRADIENT_ICON} alt={''} style={iconStyle}/>,
        },
        tooltip: i18n.t('矩形'),
        style: buttonStyle,
    },
    {
        type: FormItemType.BUTTON,
        name: FormItemName.FONT_GRADUAL_FILL_SHAPE_GRADIENT,
        options: {
            icon: <img src={SHAPE_GRADIENT_ICON} alt={''} style={iconStyle}/>,
        },
        tooltip: i18n.t('路径'),
        style: buttonStyle,
    },
    {
        type: FormItemType.ANGLE,
        name: FormItemName.FONT_GRADUAL_FILL_ANGLE,
        options: {
            ...AngleOptions,
            maxLength: 4,
        },
        style: angleStyle,
        defaultValue: 0,
    },
    {
        type: FormItemType.CUSTOM,
        name: FormItemName.FONT_GRADUAL_FILL_DIRECTION,
        content: '',
    },
    {
        type: FormItemType.CUSTOM,
        name: FormItemName.FONT_GRADUAL_FILL_BAR,
        content: '',
    },
    {
        type: FormItemType.SLIDER,
        name: FormItemName.FONT_GRADUAL_FILL_OPACITY,
        label: FormItemLabelName.OPACITY,
        options: {
            ...OpacityOptions,
        },
        defaultValue: 0,
    },
    {
        type: FormItemType.BUTTON,
        name: FormItemName.FONT_IMAGE_FILL_UPLOAD,
        options: {
            icon: <img src={UPLOAD_ICON} alt={''} style={{ marginRight: '8px' }}/>,
            text: i18n.t('选择图片'),
        },
        style: {
            color: 'var(--text-ultrastrong)',
            minWidth: 160,
        },
    },
    {
        type: FormItemType.BUTTON,
        name: FormItemName.FONT_IMAGE_FILL_CLIPBOARD,
        options: {
            text: i18n.t('剪贴板'),
        },
        style: {
            color: 'var(--text-ultrastrong)',
            minWidth: 72,
        },
    },
    {
        type: FormItemType.CUSTOM,
        name: FormItemName.FONT_IMAGE_FILL_STYLE,
        content: <div></div>,
    },
    {
        type: FormItemType.SLIDER,
        name: FormItemName.FONT_IMAGE_FILL_OPACITY,
        label: FormItemLabelName.OPACITY,
        options: {
            ...OpacityOptions,
        },
        defaultValue: 0,
    },
    {
        name: FormItemName.FONT_GRADUAL_FILL_TYPE_TEXT,
        type: FormItemType.TEXT,
        value: i18n.t('类型'),
        style: textStyle,
    },
    {
        name: FormItemName.FONT_GRADUAL_FILL_DIRECTION_TEXT,
        type: FormItemType.TEXT,
        value: i18n.t('方向'),
        style: textStyle,
    },
    {
        name: FormItemName.FONT_GRADUAL_FILL_ANGLE_TEXT,
        type: FormItemType.TEXT,
        value: i18n.t('角度'),
        style: textStyle,
    },
];
